import { useState, useEffect } from "react";
import { BrainCircuit, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from '../assets/images/logo-artie.png';

interface AnalysisLoaderProps {
  currentStep?: number; // Current step (0-2)
  statusMessage?: string; // Real-time status from backend
  projectId?: string; // To fetch real-time status
  progressPercent?: number; // Real progress from backend (0-100)
}

// Simplified steps - only guaranteed milestones that backend always hits
// Total time: 2-3.5 minutes (150-210 seconds)
const DETAILED_STEPS = [
  { label: "Initializing calculation", progressThreshold: 0, estimatedSeconds: 10 },
  { label: "Checking for existing score", progressThreshold: 10, estimatedSeconds: 15 },
  { label: "Loading script data", progressThreshold: 20, estimatedSeconds: 15 },
  { label: "Calculating Artie Score (reach, brand fit, production)", progressThreshold: 30, estimatedSeconds: 120 }, // 2 minutes for main calculation
  { label: "Saving score results", progressThreshold: 80, estimatedSeconds: 20 },
  { label: "Finalizing", progressThreshold: 95, estimatedSeconds: 10 },
  { label: "Complete!", progressThreshold: 100, estimatedSeconds: 0 }
];

export function AnalysisLoader({ currentStep: externalStep, statusMessage, projectId, progressPercent: externalProgress }: AnalysisLoaderProps) {
  const TOTAL_ESTIMATED_TIME = DETAILED_STEPS.reduce((sum, step) => sum + step.estimatedSeconds, 0);

  console.log('[AnalysisLoader] statusMessage:', statusMessage, 'progress:', externalProgress);

  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime] = useState(Date.now());
  const [progressHistory, setProgressHistory] = useState<Array<{ percent: number, time: number }>>([]);

  // Track elapsed time
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  // Reset history when the loader is re-initialized for a new calculation
  useEffect(() => {
    setProgressHistory([]);
  }, [projectId]);

  // Track progress history for rate calculation
  useEffect(() => {
    // Only add a new entry if progress has actually changed to avoid skewing the rate
    if (externalProgress !== undefined && (progressHistory.length === 0 || progressHistory[progressHistory.length - 1].percent < externalProgress)) {
      setProgressHistory(prev => {
        const newEntry = { percent: externalProgress, time: elapsedTime };
        const newHistory = [...prev, newEntry];
        // Keep last 10 data points for a rolling average
        return newHistory.slice(-10);
      });
    }
  }, [externalProgress, elapsedTime]);

  // Use real progress from backend
  const progress = externalProgress !== undefined ? externalProgress : 0;

  // Determine current step based on progress
  // Find the step we're currently IN (not the one we've completed)
  const getCurrentStepIndex = () => {
    // If at 100%, we're on the last step
    if (progress >= 100) {
      return DETAILED_STEPS.length - 1;
    }

    // Find the step whose threshold we've reached but haven't passed the next one
    for (let i = 0; i < DETAILED_STEPS.length - 1; i++) {
      const currentThreshold = DETAILED_STEPS[i].progressThreshold;
      const nextThreshold = DETAILED_STEPS[i + 1].progressThreshold;

      // We're in this step if progress is >= current threshold and < next threshold
      if (progress >= currentThreshold && progress < nextThreshold) {
        return i;
      }
    }

    // Fallback to last step before completion
    return DETAILED_STEPS.length - 2;
  };

  const currentStepIndex = getCurrentStepIndex();

  console.log('[AnalysisLoader] Progress:', progress, 'Current Step Index:', currentStepIndex, 'Step:', DETAILED_STEPS[currentStepIndex]?.label);

  // Calculate estimated time remaining with smoothing, tqdm-style
  const calculateTimeRemaining = () => {
    if (progress >= 99) return 0; // If we are at 99%, we are basically done.
    if (progress < 5 || progressHistory.length < 3) {
      // Not enough data for an accurate rate, use the initial estimate.
      const estimatedUsedTime = (TOTAL_ESTIMATED_TIME * progress) / 100;
      return Math.ceil(TOTAL_ESTIMATED_TIME - estimatedUsedTime);
    }

    // Calculate a smoothed rate of progress (percent per second)
    const first = progressHistory[0];
    const last = progressHistory[progressHistory.length - 1];
    const progressDelta = last.percent - first.percent;
    const timeDelta = last.time - first.time;

    if (progressDelta <= 0 || timeDelta <= 0) {
      // No progress made, fallback to initial estimate
      const estimatedUsedTime = (TOTAL_ESTIMATED_TIME * progress) / 100;
      return Math.ceil(TOTAL_ESTIMATED_TIME - estimatedUsedTime);
    }

    const currentRate = progressDelta / timeDelta;

    // Simple smoothing: take a weighted average of the current rate and the overall average rate
    const overallRate = progress / elapsedTime;
    const smoothingFactor = 0.4; // Adjust this to make the estimate more or less reactive
    const smoothedRate = (currentRate * smoothingFactor) + (overallRate * (1 - smoothingFactor));

    const remainingPercent = 100 - progress;
    const estimatedSeconds = remainingPercent / smoothedRate;

    // Return a sane value, preventing wild fluctuations
    return Math.min(Math.ceil(estimatedSeconds), TOTAL_ESTIMATED_TIME);
  };

  const estimatedRemaining = calculateTimeRemaining();
  const minutesRemaining = Math.floor(estimatedRemaining / 60);
  const secondsRemaining = estimatedRemaining % 60;

  return (
    <div className="flex-1 min-w-0 flex flex-col z-10 overflow-y-auto no-scrollbar bg-transparent h-full pt-8 pr-10 pb-8 pl-10 relative">

      <div className="w-full max-w-4xl mr-auto ml-auto pt-6 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-12 font-ibm">
          <span>Dashboard</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-right w-4 h-4">
            <path d="m9 18 6-6-6-6"></path>
          </svg>
          <span className="text-foreground font-medium">
            Processing Project
          </span>
        </div>

        {/* Main Content Wrapper */}
        <div className="flex flex-col w-full min-h-[50vh] max-w-2xl mx-auto">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:text-left gap-8 text-center max-w-3xl mr-auto mb-10 ml-auto gap-x-8 gap-y-8 items-center justify-center">
            <div className="w-24 h-24 relative shrink-0">
              <div className="absolute inset-0 bg-primary rounded-full blur-xl opacity-20"></div>
              <img
                src={logo}
                alt="Artie"
                className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl relative z-10"
              />
              <div className="absolute -right-8 -top-3 rotate-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap flex items-center gap-1.5 font-cabinet">
                Thinking...
                <BrainCircuit className="w-3 h-3" strokeWidth={2.5} />
              </div>
            </div>
            <div className="flex flex-col">
              <h2 className="text-3xl font-semibold text-foreground tracking-tight font-cabinet mb-3">
                Evaluating your script
              </h2>
              <p className="text-muted-foreground font-ibm text-base max-w-lg">
                Hang tight! I'm analyzing your script structure and audience appeal to calculate your Artie Score. This usually takes about 3-5 minutes.
              </p>
            </div>
          </div>

          {/* Progress Bar with Time Estimate */}
          <div className="w-full mb-10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground font-ibm">
                Progress: {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden relative">
              <div
                className="absolute top-0 left-0 h-full bg-primary rounded-full shadow-[0_0_12px_var(--primary)] transition-all duration-1000 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Detailed Status Checklist */}
          <div className="w-full space-y-3">
            {DETAILED_STEPS.map((step, index) => {
              // A step is complete only if we're past it (current step index is higher)
              const isComplete = index < currentStepIndex || (progress >= 100 && index < DETAILED_STEPS.length - 1);
              const isActive = index === currentStepIndex && progress < 100;
              const isPending = index > currentStepIndex;

              if (isComplete && progress >= 100 && index === DETAILED_STEPS.length - 1) {
                // Final completed state
                return (
                  <div key={index} className="flex items-center gap-4 p-3.5 rounded-xl bg-card border border-primary shadow-[0_4px_12px_rgba(0,0,0,0.02)]">
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" strokeWidth={3} />
                    </div>
                    <span className="text-sm font-semibold text-foreground font-ibm">
                      {step.label}
                    </span>
                    <span className="ml-auto text-xs text-primary font-ibm font-medium">
                      ✓ Done
                    </span>
                  </div>
                );
              }

              if (isComplete) {
                // Completed State
                return (
                  <div key={index} className="flex items-center gap-4 p-3.5 rounded-xl bg-card border border-border opacity-60">
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" strokeWidth={3} />
                    </div>
                    <span className="text-sm font-medium text-foreground font-ibm line-through">
                      {step.label}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground font-ibm">
                      ✓ Done
                    </span>
                  </div>
                );
              }

              if (isActive) {
                // Active State
                return (
                  <div key={index} className="flex items-center gap-4 p-3.5 rounded-xl bg-card border border-primary shadow-[0_4px_12px_rgba(0,0,0,0.02)] relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>
                    <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin shrink-0"></div>
                    <span className="text-sm font-semibold text-foreground font-ibm">
                      {step.label}
                    </span>
                    <span className="ml-auto text-xs text-primary font-ibm font-medium animate-pulse">
                      In progress...
                    </span>
                  </div>
                );
              }

              // Pending State
              return (
                <div key={index} className="flex items-center gap-4 p-3.5 rounded-xl border border-transparent opacity-50">
                  <div className="w-5 h-5 rounded-full border-2 border-border shrink-0"></div>
                  <span className="text-sm text-muted-foreground font-ibm">
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}